import React, { useContext, useState } from 'react';
import { HttpMethods, FetchContext, ApiRoutes, InsightContext, SettingsContext } from '../../../contexts';
import { useAggregatedState } from '../../../../helpers';
import { getTable } from '../../../../helpers/Utils';
import { DbType } from '../../../../helpers/Common';

export const TablesContext = React.createContext(null);

export const TableValueType = {
    Local: 0,
    Sap: 1
};

export const TablesProvider = (props) => {
    const { fetchAuthorized } = useContext(FetchContext);
    const { trackError } = useContext(InsightContext);
    const { updateLocalAppSettingS4, updateLocalAppSettingECC } = useContext(SettingsContext);

    const [state, setState] = useAggregatedState({
        tables: [],
        relatedTable: null,
        tableValues: [],
        totalValues: 0,
        accountGroupMappingsECC: [],
        accountGroupMappingsS4: [],
        selectedTableId: null,
        customerGroups6: [],
        distributionChannels: []
    });

    const [isLoadingTableDefs, setIsLoadingTableDefs] = useState(false);
    const getTableDefinitions = async (dbType, valueType) => {
        try {
            setIsLoadingTableDefs(true);

            const response = await fetchAuthorized(
                ApiRoutes.Table.GetTableDefinitions({
                    dbType,
                    valueType,
                    withRelatedTable: true,
                    withValues: false,
                    isObsolete: false
                }),
                { method: HttpMethods.GET }
            );

            const tables = await response.json();
            setState({ tables });
            setIsLoadingTableDefs(false);
        }
        catch (e) {
            console.error(e);
            trackError(e);
            setIsLoadingTableDefs(false);
        }
    };

    const getChoosenTableDefinitions = async (nameList) => {
        try {
            setIsLoadingTableDefs(true);

            const response = await fetchAuthorized(
                ApiRoutes.Table.GetChoosenTableDefinitions({
                    nameList,
                    withRelatedTable: false,
                    withValues: true,
                    isObsolete: false
                }),
                { method: HttpMethods.GET }
            );

            const tables = await response.json();
            setState({ tables });
        }
        catch (e) {
            console.error(e);
            trackError(e);
        }
        finally {
            setIsLoadingTableDefs(false);
        }
    };

    const [isLoadingRelTable, setIsLoadingRelTable] = useState(false);
    const getRelatedTable = async (tableDefinitionId, dbType) => {
        try {
            setIsLoadingRelTable(true);

            const resolve = await fetchAuthorized(
                ApiRoutes.Table.GetTableDefinition({
                    tableDefinitionId,
                    dbType,
                    withRelatedTable: false,
                    withValues: true,
                    isObsolete: false
                }),
                { method: HttpMethods.GET }
            );

            const relatedTable = await resolve.json();
            setState({ relatedTable });
            setIsLoadingRelTable(false);
        }
        catch (e) {
            console.error(e);
            trackError(e);
            setIsLoadingRelTable(false);
        }
    };

    const resetRelatedTable = () => {
        setState({ relatedTable: null });
    }

    const [isLoadingAccGroupMappingsECC, setIsLoadingAccGroupMappingsECC] = useState(false);
    const getAccountGroupMappingsECC = async () => {
        try {
            setIsLoadingAccGroupMappingsECC(true);

            const response = await fetchAuthorized(
                ApiRoutes.Table.GetAccountGroupMappingsECC(),
                { method: HttpMethods.GET }
            );

            const accountGroupMappingsECC = await response.json();
            setState({ accountGroupMappingsECC });
        }
        catch (e) {
            console.error(e);
            trackError(e);
        }
        finally {
            setIsLoadingAccGroupMappingsECC(false);
        }
    };

    const [isLoadingAccGroupMappingsS4, setIsLoadingAccGroupMappingsS4] = useState(false);
    const getAccountGroupMappingsS4 = async () => {
        try {
            setIsLoadingAccGroupMappingsS4(true);

            const response = await fetchAuthorized(
                ApiRoutes.Table.GetAccountGroupMappingsS4(),
                { method: HttpMethods.GET }
            );

            const accountGroupMappingsS4 = await response.json();
            setState({ accountGroupMappingsS4 });
        }
        catch (e) {
            console.error(e);
            trackError(e);
        }
        finally {
            setIsLoadingAccGroupMappingsS4(false);
        }
    };

    const [isLoadingTableValues, setIsLoadingTableValues] = useState(false);
    const getTableValues = async (tableId, dbType, searchText, showObsolete, pageIndex, pageSize) => {
        try {
            setIsLoadingTableValues(true);
            const table = state.tables.find(x => x.tableDefinitionId === tableId);
            const tableDefinitionId = table.tableDefinitionId;
            const resolve = await fetchAuthorized(
                ApiRoutes.Table.GetTableValues({
                    tableDefinitionId,
                    dbType,
                    searchText,
                    withRelatedValues: true,
                    isObsolete: showObsolete,
                    pageIndex: pageIndex,
                    pageSize
                }),
                {
                    method: HttpMethods.GET
                }
            );

            const list = await resolve.json();

            setState({
                tableValues: list.items,
                totalValues: list.totalItems
            });

            setIsLoadingTableValues(false);
        }
        catch (e) {
            console.error(e);
            trackError(e);
            setIsLoadingTableValues(false);
        }
    }

    const [isAddingValue, setIsAddingValue] = useState(false);
    const addTableValue = async (data) => {
        try {
            setIsAddingValue(true);

            const response = await fetchAuthorized(
                ApiRoutes.Table.AddTableValue(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data)
                }
            );

            const tableValue = await response.json();
            const tableValues = [].concat(state.tableValues);
            tableValues.push(tableValue);

            setState({ tableValues });
            setIsAddingValue(false);
        }
        catch (e) {
            console.error(e);
            trackError(e);
            setIsAddingValue(false);
        }
    };

    const [isAddingAccGroupMappingECC, setIsAddingAccGroupMappingECC] = useState(false);
    const addAccountGroupMappingECC = async (data) => {
        try {
            setIsAddingAccGroupMappingECC(true);

            const response = await fetchAuthorized(
                ApiRoutes.Table.AddAccountGroupMappingECC(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data)
                }
            );

            const mapping = await response.json();
            const mappings = [].concat(state.accountGroupMappingsECC);
            mappings.push(mapping);
            setState({ accountGroupMappingsECC: mappings });
            return true;

        }
        catch (e) {
            console.error(e);
            trackError(e);
            return false;
        }
        finally {
            setIsAddingAccGroupMappingECC(false);
        }
    };

    const [isAddingAccGroupMappingS4, setIsAddingAccGroupMappingS4] = useState(false);
    const addAccountGroupMappingS4 = async (data) => {
        try {
            setIsAddingAccGroupMappingS4(true);

            const response = await fetchAuthorized(
                ApiRoutes.Table.AddAccountGroupMappingS4(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data)
                }
            );

            const mapping = await response.json();
            const mappings = [].concat(state.accountGroupMappingsS4);
            mappings.push(mapping);
            setState({ accountGroupMappingsS4: mappings });
            return true;

        }
        catch (e) {
            console.error(e);
            trackError(e);
            return false;
        }
        finally {
            setIsAddingAccGroupMappingS4(false);
        }
    };

    const [isUpdatingAppSetting, setIsUpdatingAppSetting] = useState(false);
    const updateAppSetting = async (data) => {
        try {
            setIsUpdatingAppSetting(true)

            const response = await fetchAuthorized(
                ApiRoutes.Table.UpdateAppSetting(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data)
                }
            );

            const appSetting = await response.json();

            if (data.DbType === DbType.ECC)
                updateLocalAppSettingECC(appSetting.value, appSetting.appSettingKey)
            else
                updateLocalAppSettingS4(appSetting.value, appSetting.appSettingKey)

            setIsUpdatingAppSetting(false)

        } catch (e) {
            console.error(e);
            trackError(e);
            setIsUpdatingValue(false);
        }
    }

    const [isUpdatingValue, setIsUpdatingValue] = useState(false);
    const updateTableValue = async (data, completed) => {
        try {
            setIsUpdatingValue(true);

            const response = await fetchAuthorized(
                ApiRoutes.Table.UpdateTableValue(),
                {
                    method: HttpMethods.POST,
                    body: JSON.stringify(data)
                }
            );

            const tableValue = await response.json();
            const oldTableValue = state.tableValues.find(x => x.tableValueId === tableValue.tableValueId);
            let tableValues = [].concat(state.tableValues);

            if (oldTableValue.isObsolete != tableValue.isObsolete) {
                const values = tableValues.filter(x => x.tableValueId !== tableValue.tableValueId);
                tableValues = values;
            }
            else {
                tableValues[tableValues.findIndex(x => x.tableValueId === tableValue.tableValueId)] = tableValue;
            }

            setState({ tableValues });
            setIsUpdatingValue(false);
        }
        catch (e) {
            console.error(e);
            trackError(e);
            setIsUpdatingValue(false);
        }
    };

    const [isDeletingValue, setIsDeletingValue] = useState(false);
    const deleteTableValue = async (tableValueId, dbType) => {
        try {
            setIsDeletingValue(true);

            const _ = await fetchAuthorized(
                ApiRoutes.Table.DeleteTableValue({ tableValueId, dbType }),
                { method: HttpMethods.DELETE }
            );

            const tableValues = [].concat(state.tableValues.filter(x => x.tableValueId !== tableValueId));

            setState({ tableValues });
            setIsDeletingValue(false);
        }
        catch (e) {
            console.error(e);
            trackError(e);
            setIsDeletingValue(false);
        }
    };

    const [isDeletingAccGroupMapping, setIsDeletingAccGroupMapping] = useState(false);
    const deleteAccountGroupMapping = async (mappingId, dbType) => {
        try {
            setIsDeletingAccGroupMapping(true);

            const _ = await fetchAuthorized(
                ApiRoutes.Table.DeleteAccountGroupMapping({ mappingId, dbType }),
                { method: HttpMethods.DELETE }
            );

            if (dbType === DbType.ECC){
                const accountGroupMappingsECC = [].concat(state.accountGroupMappingsECC.filter(x => x.accountGroupMappingId !== mappingId));
                setState({ accountGroupMappingsECC });
            }
            else {
                const accountGroupMappingsS4 = [].concat(state.accountGroupMappingsS4.filter(x => x.accountGroupMappingId !== mappingId));
                setState({ accountGroupMappingsS4 });
            }

            setIsDeletingAccGroupMapping(false);
        }
        catch (e) {
            console.error(e);
            trackError(e);
            setIsDeletingAccGroupMapping(false);
        }
    };

    const getAccountGroupMappingsValueName = (tableName, valueId, dbType) => {
        if (state.tables && state.tables.length > 0) {
            const table = getTable(tableName, state);

            if (table && (table.childValues_ECC || table.childValues_S4)) {
                const childValues = dbType === DbType.ECC ?
                    table.childValues_ECC :
                    table.childValues_S4;

                const value = childValues.find(x => x.tableValueId === valueId);
                return value ? value.value : undefined;
            }
            else
                return "";
        }
        else
            return "";
    };

    const getAccountGroupMappingsValueNameCode = (tableName, valueId, dbType) => {
        if (state.tables && state.tables.length > 0) {
            const table = getTable(tableName, state);

            if (table && (table.childValues_ECC || table.childValues_S4)) {
                const childValues = dbType === DbType.ECC ?
                    table.childValues_ECC :
                    table.childValues_S4;

                const value = childValues.find(x => x.tableValueId === valueId);
                return value ? value.externalKey + " - " + value.value : undefined;
            }
            else
                return "";
        }
        else
            return "";
    };

    const setSelectedTableId = (tableId) => {
        setState({ selectedTableId: tableId });
    };

    const getDistributionChannels = async (customerGroup6Id) => {
        if (customerGroup6Id) {
            try {
                const response = await fetchAuthorized(
                    ApiRoutes.Request.GetDistributionChannels({ customerGroup6Id }),
                    { method: HttpMethods.GET }
                );

                const distributionChannels = await response.json();
                setState({ distributionChannels });
            }
            catch (e) {
                console.error(e);
                trackError(e);
            }
        }
    };

    const { Provider } = TablesContext;

    return (
        <Provider value={{
            state,
            isLoadingTableDefs,
            getTableDefinitions,
            getChoosenTableDefinitions,

            isLoadingRelTable,
            getRelatedTable,
            resetRelatedTable,

            isLoadingTableValues,
            getTableValues,

            isLoadingAccGroupMappingsECC,
            getAccountGroupMappingsECC,

            isLoadingAccGroupMappingsS4,
            getAccountGroupMappingsS4,

            isAddingValue,
            addTableValue,

            isAddingAccGroupMappingECC,
            addAccountGroupMappingECC,

            isAddingAccGroupMappingS4,
            addAccountGroupMappingS4,

            isUpdatingValue,
            updateTableValue,

            isUpdatingAppSetting,
            updateAppSetting,

            isDeletingValue,
            deleteTableValue,

            isDeletingAccGroupMapping,
            deleteAccountGroupMapping,

            getAccountGroupMappingsValueName,
            getAccountGroupMappingsValueNameCode,

            setSelectedTableId,
            getDistributionChannels
        }}>
            {props.children}
        </Provider>
    );
};